import React from 'react';
import { Table, Button } from 'react-bootstrap';
import './DataTable.css';

const DataTable = ({
  title,
  data,
  columns,
  actions,
  searchable = true,
  pagination = true,
  exportable = false,
  onExport,
  rowsPerPageOptions = [5, 10, 25, 50],
  customActions,
  sortConfig,
  onSort,
}) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [currentPage, setCurrentPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(rowsPerPageOptions[1]);

  const filteredData = React.useMemo(() => {
    return data.filter(item =>
      Object.values(item).some(value =>
        value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [data, searchTerm]);

  const paginatedData = React.useMemo(() => {
    const start = currentPage * rowsPerPage;
    return filteredData.slice(start, start + rowsPerPage);
  }, [filteredData, currentPage, rowsPerPage]);

  return (
    <div className="datatable-container">
      <div className="datatable-header">
        <h2>{title}</h2>
        <div className="datatable-actions">
          {searchable && (
            <div className="search-box">
              <i className="fas fa-search search-icon"></i>
              <input
                type="text"
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          )}
          {customActions}
          {exportable && (
            <button className="export-button" onClick={onExport}>
              <i className="fas fa-download"></i>
              Descargar Lista
            </button>
          )}
        </div>
      </div>

      <div className="datatable-wrapper">
        <Table hover className="datatable">
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  onClick={() => column.sortable && onSort?.(column.key)}
                  className={column.sortable ? 'sortable' : ''}
                >
                  {column.label}
                  {column.sortable && sortConfig?.key === column.key && (
                    <i className={`fas fa-sort-${sortConfig.direction}`}></i>
                  )}
                </th>
              ))}
              {actions && <th className="actions-column">Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((item, index) => (
              <tr key={item.id || index}>
                {columns.map((column) => (
                  <td key={column.key}>
                    {column.render ? column.render(item) : item[column.key]}
                  </td>
                ))}
                {actions && (
                  <td className="actions-cell">
                    {actions.map((action, actionIndex) => (
                      <Button
                        key={actionIndex}
                        variant={action.variant}
                        size="sm"
                        className="action-btn"
                        onClick={() => action.onClick(item)}
                        title={action.title}
                      >
                        <i className={action.icon}></i>
                        {action.label && <span>{action.label}</span>}
                      </Button>
                    ))}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {pagination && (
        <div className="datatable-footer">
          <div className="rows-per-page">
            <span>Mostrar:</span>
            <select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(0);
              }}
            >
              {rowsPerPageOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            <span>registros</span>
          </div>
          <div className="pagination-controls">
            <Button
              className="pagination-btn"
              onClick={() => setCurrentPage(0)}
              disabled={currentPage === 0}
              title="Primera página"
            >
              <i className="fas fa-step-backward"></i>
            </Button>
            <Button
              className="pagination-btn"
              onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
              disabled={currentPage === 0}
              title="Página anterior"
            >
              <i className="fas fa-chevron-left"></i>
            </Button>
            <span className="page-info">
              Página {currentPage + 1} de {Math.ceil(filteredData.length / rowsPerPage)}
            </span>
            <Button
              className="pagination-btn"
              onClick={() => setCurrentPage(p => p + 1)}
              disabled={(currentPage + 1) * rowsPerPage >= filteredData.length}
              title="Página siguiente"
            >
              <i className="fas fa-chevron-right"></i>
            </Button>
            <Button
              className="pagination-btn"
              onClick={() => setCurrentPage(Math.ceil(filteredData.length / rowsPerPage) - 1)}
              disabled={(currentPage + 1) * rowsPerPage >= filteredData.length}
              title="Última página"
            >
              <i className="fas fa-step-forward"></i>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable; 